import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, InputNumber, Tag, Spin, message, Divider } from 'antd';
import { ShoppingCartOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { productAPI, cartAPI } from '../services/api';
import { isAuthenticated } from '../utils/auth';
import './ProductDetail.css';

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const res = await productAPI.getProductById(id);
      setProduct(res.data);
    } catch (error) {
      message.error('加载商品失败');
      navigate('/mall');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated()) {
      message.warning('请先登录');
      navigate('/login');
      return;
    }

    if (quantity > product.stock) {
      message.warning('库存不足');
      return;
    }

    setAddingToCart(true);
    try {
      await cartAPI.addToCart({ product_id: product.id, quantity });
      message.success('已添加到购物车，正在打开购物车');
      window.dispatchEvent(new Event('cartUpdate'));
      navigate('/cart');
    } catch (error) {
      message.error(error.message || '添加失败');
    } finally {
      setAddingToCart(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const getTypeTag = (type) => {
    const typeMap = {
      physical: { color: 'blue', text: '实物商品' },
      virtual: { color: 'purple', text: '虚拟商品' },
      service: { color: 'green', text: '服务' }
    };
    return typeMap[type] || typeMap.physical;
  };

  const typeInfo = getTypeTag(product.type);

  return (
    <div className="product-detail-container">
      <div className="detail-header">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/mall')}
        >
          返回商城
        </Button>
      </div>

      <Card className="product-card">
        <div className="product-detail-content">
          <div className="product-images">
            <img
              src={product.main_image || '/images/default-product.svg'}
              alt={product.name}
              onError={(e) => { e.target.src = '/images/default-product.svg'; }}
            />
          </div>

          <div className="product-info">
            <div className="product-tags">
              <Tag color={typeInfo.color}>{typeInfo.text}</Tag>
              <Tag color="blue">{product.category_name}</Tag>
            </div>

            <h1 className="product-name">{product.name}</h1>

            <div className="product-price-section">
              <div className="price-container">
                <span className="current-price">¥{product.price}</span>
                {product.original_price && product.original_price > product.price && (
                  <span className="original-price">¥{product.original_price}</span>
                )}
              </div>
              <div className="product-stats-line">
                <span>库存: {product.stock}</span>
                <span>已售: {product.sales || 0}</span>
              </div>
            </div>

            <Divider />

            <div className="product-description">
              <h3>商品介绍</h3>
              <p>{product.description || '暂无描述'}</p>
            </div>

            <Divider />

            <div className="purchase-section">
              <div className="quantity-selector">
                <span className="label">购买数量:</span>
                <InputNumber
                  min={1}
                  max={product.stock}
                  value={quantity}
                  onChange={setQuantity}
                  disabled={product.stock === 0}
                />
                <span className="stock-hint">
                  （库存: {product.stock}）
                </span>
              </div>

              <div className="action-buttons">
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingCartOutlined />}
                  loading={addingToCart}
                  onClick={handleAddToCart}
                  disabled={product.stock === 0}
                  block
                >
                  {product.stock === 0 ? '已售罄' : '加入购物车'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default ProductDetail;
